import {
  IAdminPackageItem,
  IAdminPackageResponse,
  IAdminPackagesResponse,
  IProductPackageMutationPayload,
  IProductPackageToolPayload,
  IProductPlanPackage,
} from '../../models/ProductPlanPackage';
import { IProductPackageVariantPayload } from '../../models/PackageDuration';
import { productPackageResources } from '../builders/productPackageResources';
import { getUseResource } from '../resource';

/** Request all packages at once — consumers (grids, transfer lists) expect the full list. */
const getPackagesQueryParams = () => ({ page: 1, page_size: 1000 });

const mapPackage = (item: IAdminPackageItem): IProductPlanPackage => ({
  id: item.id,
  legacyId: item.legacyId,
  name: item.name,
  created: new Date(item.createdAt).getTime(),
  updated: new Date(item.updatedAt).getTime(),
  isAddon: item.isAddon,
  isWatermark: item.isWatermark,
  baseDailyPrice: item.baseDailyPrice == null ? null : Number(item.baseDailyPrice),
  productId: item.products?.[0]?.id ?? null,
  tools: (item.tools ?? []).map((tool) => ({
    toolId: tool.toolId,
    name: tool.name,
    quantity: tool.quantity,
  })),
  variants: (item.variants ?? []).map((variant) => ({
    durationType: variant.durationType,
    durationDays: variant.durationDays,
    discountPercentage: Number(variant.discountPercentage),
    totalPrice: Number(variant.totalPrice),
    archived: variant.archived,
  })),
});

const mapPackageMutationResponse = (body: IAdminPackageResponse): IProductPlanPackage => mapPackage(body.data);

/**
 * Maps the frontend payload to the drmrust create/update body.
 * The computed prices and the generated id are rejected as input, so they are never sent.
 */
const mapPackageMutationRequest = (payload: IProductPackageMutationPayload) => {
  const body: {
    name: string;
    tools: IProductPackageToolPayload[];
    variants: IProductPackageVariantPayload[];
    isWatermark: boolean;
    productId?: string;
    baseDailyPrice?: number;
    isAddon?: boolean;
  } = {
    name: payload.name,
    tools: payload.tools,
    variants: payload.variants,
    isWatermark: payload.isWatermark ?? false,
  };

  // Left out rather than nulled — on update the API reads an absent key as "leave untouched".
  if (payload.productId) {
    body.productId = payload.productId;
  }

  if (payload.baseDailyPrice !== undefined && payload.baseDailyPrice !== '') {
    body.baseDailyPrice = Number(payload.baseDailyPrice);
  }

  if (payload.isAddon !== undefined) {
    body.isAddon = payload.isAddon;
  }

  return body;
};

export const apiProductPackage = {
  useGetPackages: getUseResource(
    productPackageResources.getPackages,
    (body: IAdminPackagesResponse): { packages: IProductPlanPackage[] } => ({
      packages: body.data.data.map(mapPackage),
    }),
    getPackagesQueryParams
  ),
  useCreatePackage: getUseResource(
    productPackageResources.createPackage,
    mapPackageMutationResponse,
    (params, payload: IProductPackageMutationPayload) => mapPackageMutationRequest(payload)
  ),
  useEditPackage: getUseResource(
    productPackageResources.updatePackage,
    mapPackageMutationResponse,
    (params, payload: IProductPackageMutationPayload) => mapPackageMutationRequest(payload)
  ),
  useDeletePackage: getUseResource(productPackageResources.deletePackage, () => null),
};
